import React from 'react';
import { cn } from '@/lib/utils';

export interface FigureProps {
  label: string;
  value: string;
  /** What the figure is measured against — a number is never shown naked (Design Principle 2). */
  reference?: React.ReactNode;
  /** The 6px dot that carries income / expense identity; the figure itself stays neutral. */
  anchor?: 'income' | 'expense';
  className?: string;
}

// Secondary Figure (DESIGN.md): Bricolage, text-foreground; semantic color only as the dot in the label.
export const Figure: React.FC<FigureProps> = ({ label, value, reference, anchor, className }) => (
  <div className={className}>
    <dt className="mb-1.5 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
      {anchor && (
        <span
          className={cn('inline-block h-1.5 w-1.5 rounded-full', anchor === 'income' ? 'bg-income' : 'bg-expense')}
          aria-hidden="true"
        />
      )}
      {label}
    </dt>
    <dd className="font-display text-[clamp(1.4rem,3.2vw,1.875rem)] font-bold leading-[1.05] tracking-tight tabular-nums text-foreground">
      {value}
    </dd>
    {reference && <dd className="mt-1.5 text-sm tabular-nums text-muted-foreground">{reference}</dd>}
  </div>
);

export interface FigureStripProps {
  label: string;
  children: React.ReactNode;
  /** A line under the figures, for a fact the numbers alone don't say. */
  note?: React.ReactNode;
}

// The three figures that open each section. Both use this strip, so the two read alike stacked one above the
// other; the third takes a full row on a phone (`col-span-2 sm:col-span-1` on the Figure).
export const FigureStrip: React.FC<FigureStripProps> = ({ label, children, note }) => (
  <section aria-label={label} className="border-b border-border/60 pb-6">
    <dl className="grid grid-cols-2 gap-x-10 gap-y-6 sm:grid-cols-3 sm:gap-x-12">{children}</dl>
    {note}
  </section>
);
